import React, { memo } from "react";

import { mapToApiPage } from "./helpers";
import { Menu as RawMenu } from "./Menu";
import { PagePreview as RawPagePreview } from "./PagePreview";
import { Renderer as RawRenderer } from "./Renderer";
import { Size } from "./Renderer/types";
import { StateManager } from "./StateManager";
import { ApiZone, JobState, SavePage, SubmitJob, URI, ZoningTaxonomy } from "./types";

import { SavePageOnSave } from "./SavePageOnSave";
import { PageSaveState } from "./StateManager/state";
import "./styles.scss";

const CONFIRM_RESET_MESSAGE =
  "Are you sure you want to reset this page? All your changes will be reverted.";
const CONFIRM_SUBMIT_MESSAGE =
  "Are you sure you want to submit this job? Please ensure all pages have been reviewed.";

// memoize the major components for better performance
const Menu = memo(RawMenu);
const PagePreview = memo(RawPagePreview);
const Renderer = RawRenderer;

type Props = Readonly<{
  autoShrink: boolean;
  highConfidenceThreshold: number;
  zoningTaxonomyName: string;
  highResImageURIs: Array<URI>;
  initialZones: Array<Array<ApiZone>>;
  jobState: JobState;
  lowResImageURIs: Array<URI>;
  noteCount: number;
  notes: React.ReactElement;
  pageConfidences: Array<number>;
  pageDimensions: Array<Size>;
  readonly: boolean;
  resetToZones: Array<Array<ApiZone>> | undefined;
  savePage: SavePage;
  submitJob: SubmitJob;
  taxonomy: ZoningTaxonomy;
}>;

export const Zoning = ({
  autoShrink,
  highConfidenceThreshold,
  zoningTaxonomyName,
  highResImageURIs,
  initialZones,
  jobState,
  lowResImageURIs,
  noteCount,
  notes,
  pageConfidences,
  pageDimensions,
  readonly,
  resetToZones,
  savePage,
  submitJob,
  taxonomy,
}: Props) => {
  return (
    <StateManager
      highConfidenceThreshold={highConfidenceThreshold}
      zoningTaxonomyName={zoningTaxonomyName}
      initialZones={initialZones}
      pageConfidences={pageConfidences}
      pageDimensions={pageDimensions}
      readonly={readonly}
      taxonomy={taxonomy}
    >
      {({ dispatch, state }) => {
        const onSave = () => {
          dispatch({
            completed: false,
            failed: false,
            page: state.activePage,
            type: "SAVE PAGE",
          });
          savePage(
            state.activePage + 1,
            mapToApiPage(state.taxonomy)(state.pages[state.activePage]),
            true,
            false
          )
            .then(() => {
              dispatch({
                completed: true,
                failed: false,
                page: state.activePage,
                type: "SAVE PAGE",
              });
            })
            .catch(() => {
              dispatch({
                completed: true,
                failed: true,
                page: state.activePage,
                type: "SAVE PAGE",
              });
            });
        };
        return (
          <div className="zoning">
            <SavePageOnSave
              dispatch={dispatch}
              pages={state.pages}
              previousPage={state.previousPage}
              savePage={savePage}
              taxonomy={state.taxonomy}
            />
            <Menu
              zoningTaxonomyName={zoningTaxonomyName}
              activeLabel={state.currentLabel}
              currentLabelGroup={state.currentLabelGroup}
              dispatch={dispatch}
              jobState={jobState}
              onReset={() => {
                if (!Array.isArray(resetToZones) || resetToZones.length < state.activePage) {
                  return;
                }
                if (!window.confirm(CONFIRM_RESET_MESSAGE)) {
                  return;
                }
                dispatch({
                  page: state.activePage,
                  type: "RESET ZONES",
                  zones: resetToZones[state.activePage],
                });
              }}
              onSave={onSave}
              onSubmit={() => {
                if (!window.confirm(CONFIRM_SUBMIT_MESSAGE)) {
                  return;
                }
                savePage(
                  state.activePage + 1,
                  mapToApiPage(state.taxonomy)(state.pages[state.activePage]),
                  false,
                  true
                ).then(() => {
                  submitJob();
                });
              }}
              pageModified={state.pages[state.activePage].pageSaveState === PageSaveState.Dirty}
              readonly={readonly}
              taxonomy={state.taxonomy}
              state={state}
            />
            <Renderer
              autoShrink={autoShrink}
              dispatch={dispatch}
              jobState={jobState}
              onSave={onSave}
              pageImageURIs={highResImageURIs}
              readonly={readonly}
              state={state}
            />
            <PagePreview
              activePage={state.activePage}
              dispatch={dispatch}
              highConfidenceThreshold={highConfidenceThreshold}
              noteCount={noteCount}
              notes={notes}
              pageConfidences={pageConfidences}
              pageImageURIs={lowResImageURIs}
            />
          </div>
        );
      }}
    </StateManager>
  );
};
Zoning.displayName = "Zoning";
